/**
 * framework.js - GAME-LEVEL-ENGINE canonical data
 *
 * Source of truth: docs/GAME-LEVEL-ENGINE.md v1.0 (CANONICAL).
 * Level data extracted from client/public/block-builder.html, the only
 * implementation the audit (docs/audit/LEVEL-COUNT-INVENTORY.md section 3)
 * verified as spec-conformant at 53 levels.
 *
 * This module is DATA ONLY. It holds no state, touches no DOM, makes no
 * network or database calls. Games read it; nothing writes to it.
 *
 * ---------------------------------------------------------------------------
 * THE THREE NUMBERS (section 5.1)
 *   10 phases  .  53 levels  .  208 boxes
 * They are asserted at the bottom of this file. If an edit breaks one of them,
 * the module throws at import time rather than shipping a wrong chart.
 * ---------------------------------------------------------------------------
 *
 * BOX IDS ARE PERMANENT
 * Every box carries an id of the form P<pp>L<ll><C> - zero-padded phase,
 * zero-padded level, single uppercase component code. Examples:
 *
 *     P01L01A   phase 1, level 1, component A (Attends to Model)
 *     P08L06E   phase 8, level 6, component E (Maintains Topic)
 *
 * These strings are database keys (see database/engine_schema.sql,
 * box_score.box_id). They MUST NEVER be renumbered, reassigned or reused.
 * Rules for any future change:
 *   - Adding a level to a phase appends new ids; existing ids do not move.
 *   - Adding a component appends a new letter; existing letters do not move.
 *   - Retiring a box means marking it retired here, never deleting the id
 *     and never handing the letter to a different skill.
 *   - The format sorts lexicographically into exact chart order (phase
 *     column, then level row, then box left-to-right), so ORDER BY box_id
 *     in SQL renders the chart correctly with no join.
 *
 * ---------------------------------------------------------------------------
 * BOX ARITHMETIC - how 208 is reached (section 5.2)
 *
 *   Phase  Levels  Components                              Boxes
 *     1      5     A,B,C                                    15
 *     2      6     A,B,C                                    18
 *     3      6     A,B,C,D                                   24
 *     4      6     A,B,C,D                                   24
 *     5      5     A,B,C,D                                   20
 *     6      5     A,B,C,D                                   20
 *     7      5     A,B,C,D all levels + E on levels 3-5      23
 *     8      6     A,B,C,D all levels + E on levels 3-6      28
 *     9      5     A,B,C,D                                   20
 *    10      4     A,B,C,D                                   16
 *                                                          -----
 *                                                            208
 *
 * Phase 7 is documented: section 6.8 marks component E "Third Command" as
 * "(Levels 3-5)", which gives 5*4 + 3 = 23, matching section 5.2's 23.
 *
 * Phase 8 is NOT documented. Section 5.2 requires 28 boxes over 6 levels;
 * section 6.9 lists five components (A-E) but says nothing about which
 * levels carry E. 6*4 = 24 and 6*5 = 30, so E must apply to exactly four of
 * the six levels. This file resolves that as levels 3-6, mirroring Phase 7's
 * documented pattern of E entering partway up the column and staying.
 * It is also the clinically sensible reading: "Maintains Topic" cannot be
 * assessed on Phase 8 Level 1 (yes/no answers) or Level 2 (single "what"
 * answers) because neither exchange is long enough to stay on or wander off
 * topic. It becomes measurable at Level 3 ("where" answers, which can be
 * wrong-but-relevant or simply off-topic) and is essential by Level 6
 * (multi-turn conversation).
 *
 * This resolution is an ENGINE DECISION recorded here, not a quotation from
 * the spec. If the spec is later amended to place E elsewhere, the affected
 * ids change and the migration must be handled explicitly - see
 * docs/audit/ENGINE-BUILD.md.
 * ---------------------------------------------------------------------------
 */

export const FRAMEWORK_VERSION = '1.0.0';
export const SPEC_DOCUMENT = 'docs/GAME-LEVEL-ENGINE.md v1.0';

/* =========================================================================
 * COLOR SYSTEM (section 7)
 * Colors represent QUALITY OF MASTERY, not assessment dates.
 * `rank` orders them for the "lowest box color wins" rule (section 7.5).
 * `gray` is not in the spec's five-colour table; section 7.6 defines the
 * unplayed state and this engine ranks it below red so that a level
 * containing any unassessed box reads as unassessed, not as failing.
 * ========================================================================= */

export const COLORS = {
  gray:   { id: 'gray',   rank: 0, name: 'Not Assessed', label: 'Not enough plays yet', min: null, max: null, hex: '#C9CDD4' },
  red:    { id: 'red',    rank: 1, name: 'Needs Support', label: 'Needs Support',        min: 0,    max: 2,    hex: '#E5484D' },
  yellow: { id: 'yellow', rank: 2, name: 'Emerging',      label: 'Emerging',             min: 3,    max: 4,    hex: '#F2C94C' },
  blue:   { id: 'blue',   rank: 3, name: 'Developing',    label: 'Developing',           min: 5,    max: 6,    hex: '#3E8BFF' },
  purple: { id: 'purple', rank: 4, name: 'Strong',        label: 'Strong',               min: 7,    max: 8,    hex: '#8B5CF6' },
  green:  { id: 'green',  rank: 5, name: 'Mastered',      label: 'Mastered',             min: 9,    max: 10,   hex: '#22C55E' },
};

/** Scoring window: first-attempt successes counted over the last N plays (section 7.4). */
export const SCORING_WINDOW = 10;

/** Colours in chart order, lowest first. Excludes gray. */
export const COLOR_ORDER = ['red', 'yellow', 'blue', 'purple', 'green'];

/* =========================================================================
 * PROMPT SUPPORT LADDER
 *
 * Section 2.5 mandates most-to-least prompt fading; section 8.2 names the
 * prompt style of each phase. Neither gives the ladder a numeric scale, so
 * this is the engine's encoding of the styles section 8.2 names, ordered
 * from most support (0) to least (7). progression.js moves a child up and
 * down this ladder; it never invents a rung.
 *
 * ENGINE DEFAULT - the rung names come from section 8.2, the ordering and
 * the per-phase bounds below do not. Tunable without touching the spec.
 * ========================================================================= */

export const SUPPORT_LADDER = [
  { index: 0, id: 'full_model',     name: 'Full prompt + audio model', detail: 'Pip says the target word aloud; child repeats.' },
  { index: 1, id: 'partial_verbal', name: 'Partial verbal',            detail: 'Part of the word shown or sounded ("A___").' },
  { index: 2, id: 'visual',         name: 'Visual',                    detail: 'Word frames, object choices, image highlight.' },
  { index: 3, id: 'gestural',       name: 'Gestural',                  detail: 'Pip points; no words offered.' },
  { index: 4, id: 'positional',     name: 'Positional',                detail: 'Target grid position highlighted.' },
  { index: 5, id: 'time_delay',     name: 'Time delay',                detail: 'Hint appears only after a wait.' },
  { index: 6, id: 'natural',        name: 'Natural / situation only',  detail: 'The situation is the prompt; Pip may model once.' },
  { index: 7, id: 'independent',    name: 'None',                      detail: 'No help. Child performs unaided.' },
];

export const SUPPORT_BY_ID = Object.freeze(
  SUPPORT_LADDER.reduce((acc, rung) => { acc[rung.id] = rung; return acc; }, {})
);

/* =========================================================================
 * RECOGNITION STRICTNESS (section 8.3)
 *
 * `phases` and `accepts` are quoted from the spec.
 * `rules` are ENGINE DEFAULTS - the spec sets no numeric thresholds. They
 * are data so they can be tuned per-deployment without a code change.
 *
 * minConfidence is the floor below which a SpeechRecognition result is not
 * trusted enough to score. It is deliberately not monotonic: phases 7-10
 * sit below phases 5-6 because natural conversational speech is longer and
 * noisier, and the browser returns lower confidence on it. Scoring a long
 * true answer as "unheard" is the worse failure there.
 * ========================================================================= */

export const STRICTNESS_BANDS = {
  loose: {
    id: 'loose',
    phases: [1, 2],
    accepts: 'Approximations OK ("ap" for apple)',
    rules: {
      allowApproximation: true,
      requireCoreWords: false,
      requireWordOrder: false,
      requireStructure: false,
      minPrefixChars: 2,
      minConfidence: 0.30,
    },
  },
  moderate: {
    id: 'moderate',
    phases: [3, 4],
    accepts: 'Core words must be present',
    rules: {
      allowApproximation: false,
      requireCoreWords: true,
      requireWordOrder: false,
      requireStructure: false,
      minPrefixChars: null,
      minConfidence: 0.45,
    },
  },
  standard: {
    id: 'standard',
    phases: [5, 6],
    accepts: 'Phrase structure required',
    rules: {
      allowApproximation: false,
      requireCoreWords: true,
      requireWordOrder: true,
      requireStructure: true,
      minPrefixChars: null,
      minConfidence: 0.55,
    },
  },
  flexible: {
    id: 'flexible',
    phases: [7, 8, 9, 10],
    accepts: 'Complete thoughts, natural speech',
    rules: {
      allowApproximation: false,
      requireCoreWords: true,
      requireWordOrder: false,
      requireStructure: false,
      acceptSemanticEquivalents: true,
      minPrefixChars: null,
      minConfidence: 0.50,
    },
  },
};

/* =========================================================================
 * BOX STATES AND GAME CAPABILITIES
 *
 * Not every box can be coloured. Three states exist alongside the five
 * colours, and they mean different things:
 *
 *   'gray'             assessable, simply not played 10 times yet (section 7.6)
 *   'observational'    PERMANENT. No browser input can produce this judgement.
 *                      Identical in every game - see below.
 *   'not_instrumented' this game does not record the signal the box needs.
 *                      Per-game and temporary; another game may score it.
 *
 * ---------------------------------------------------------------------------
 * OBSERVATIONAL BOXES - 21 of 208, framework-level and permanent
 *
 * Phase 1 A (Attends to Model), Phase 8 A (Attends), Phase 4 C (Articles),
 * Phase 10 D (Generalized). Rationale per box in `observationalReason`; the
 * full analysis is in docs/audit/COMPONENT-JUDGEMENT.md.
 *
 * The first three are boxes no SENSOR can reach. Phase 10 D is here for a
 * different reason and it is worth keeping the distinction visible: it is
 * perfectly measurable, just not from one utterance. It is section 9.3's
 * cross-game verification, which the chart already computes, and leaving it
 * scoreable obliged every Phase 10 game to invent a per-play proxy for a fact
 * about the chart (docs/audit/FRAMEWORK-GAPS.md section 1).
 *
 * These are a property of the SKILL and the available sensors, not of any
 * game. Section 9.1 requires the chart to mean the same thing in every game,
 * so this is fixed here and a game cannot declare it, override it, or opt out.
 * A game that adds a camera does not get to re-colour Phase 1 A, because then
 * a Green in that game would not mean what Green means everywhere else.
 *
 * ---------------------------------------------------------------------------
 * INSTRUMENTATION CAPABILITIES - 28 of 208, per-game
 *
 * These boxes ARE measurable, but only from signals a game must choose to
 * record. A game declares what it instruments; boxes whose requirement is
 * unmet read 'not_instrumented' in that game and are excluded from that game's
 * level colour. They still read gray on the child-global chart until some game
 * scores them, because globally they are unassessed, not inapplicable.
 *
 * A capability can ONLY affect a box carrying a matching `requires` field.
 * There is no mechanism to declare away an observational box or a directly
 * measurable one, and there must never be one.
 * ========================================================================= */

export const BOX_STATES = {
  OBSERVATIONAL: 'observational',
  NOT_INSTRUMENTED: 'not_instrumented',
};

/* =========================================================================
 * CONDITIONAL BOXES - assessable, but not on every trial
 *
 * A third thing a box can be, distinct from both states above. An
 * observational box can never be scored by anyone; a not-instrumented box
 * cannot be scored by THIS GAME; a conditional box can be scored by any game
 * that instruments it, on the trials where the question it asks is answerable.
 *
 * The example, and at present the only one: Phase 3 D "Spontaneous". On a
 * trial where the target was never going to be modelled, "did not echo" is
 * free - there was nothing to echo. Scored anyway it converges to Green while
 * measuring nothing, which is worse than not scoring it, because a therapist
 * reading Green there reads "produces spontaneously".
 *
 * THREE RULES, and they are what keep this from becoming a hole in section 8.1
 * rule 3 (every component of the level must be measured):
 *
 * 1. Only the FRAMEWORK may declare a box conditional. A game cannot mark a
 *    box inapplicable, for the same reason capabilities are declared per build
 *    and read from the registry rather than the request: a chart that means one
 *    thing when a field happens to be present and another when it does not is
 *    worse than one that means nothing.
 * 2. The ENGINE decides applicability, from the trial record. The game
 *    supplies evidence - was a model scheduled, did the child speak - exactly
 *    as it already does for the 28 instrumentation boxes; it does not supply
 *    the verdict.
 * 3. A not-applicable outcome is EXCLUDED from the box's ten-play window
 *    rather than stored as a failure. The window is ten APPLICABLE plays. Such
 *    a box fills more slowly, which is true, and the chart should show it as
 *    n/10 rather than hide it.
 * ========================================================================= */

export const CONDITIONALS = {
  model_opportunity: {
    id: 'model_opportunity',
    name: 'A model was on offer and the child spoke',
    question:
      'Did this trial both offer the target as a model and produce an utterance? '
      + 'If Pip was never going to say it, "did not echo it" is free.',
    decidedBy: 'engine',
    from: ['modelScheduled', 'modelPlayed', 'speechStartedAt'],
  },
};

export const CONDITIONAL_IDS = Object.keys(CONDITIONALS);

/* =========================================================================
 * COMPONENT ROLES - what a box is FOR
 *
 * Every component measures something, and section 7 treats them all alike
 * because the chart treats them alike: ten first attempts, one colour each,
 * lowest wins. That is right for the chart and wrong for PACING, and the
 * difference is not cosmetic.
 *
 * Section 8.4 asks whether the child is succeeding, and the only definition
 * available was "every box of this level succeeded". At Phase 2 the three
 * boxes are the same event seen at three points on the fading curve - A is
 * "said the word", B and C are "and needed less help". A child placed by
 * section 9.2 at the full model who says the word PERFECTLY, every time,
 * scores A true and B and C false, so "every box" reads 0%, so the engine
 * suggests stepping them back a level - out of Phase 2 and into Phase 1,
 * where they succeed, are advanced, and arrive back at Phase 2 to be sent
 * away again. A simulated session found the loop; a child would have lived it.
 *
 * So a box declares what it is for:
 *
 *   outcome        the child did the thing the level asks
 *   partial        a component of that thing, on its own
 *   attempt        the child tried - Phase 1 B, the pre-verbal box
 *   independence   how much HELP the child needed, not what they produced
 *   observational  nobody can measure it
 *
 * PACING reads everything except `independence` (scoring.attemptSucceeded).
 * How much help a child needed is what the support ladder is for: it drives
 * section 2.5's fade through `unaided`, and it must not also decide whether
 * the child is failing, or the two rules fight and the child loses.
 *
 * THE CHART IS UNCHANGED. Every box still colours independently and a level is
 * still its lowest box (section 7.5). This says nothing about mastery.
 * ========================================================================= */

export const COMPONENT_ROLES = Object.freeze({
  OUTCOME: 'outcome',
  PARTIAL: 'partial',
  ATTEMPT: 'attempt',
  INDEPENDENCE: 'independence',
  OBSERVATIONAL: 'observational',
});

export const ROLE_IDS = Object.freeze(Object.values(COMPONENT_ROLES));

export const CAPABILITIES = {
  prompt_sequence: {
    id: 'prompt_sequence',
    name: 'Within-trial prompt sequence',
    detail:
      'The game escalates support within a single trial (silence -> partial -> full) ' +
      'and records which rung was live at speech onset, whether a hint had fired, ' +
      'and whether Pip had modelled the target.',
    boxes: 23,
    unlocks: 'Phase 2 A/B/C (18 boxes), Phase 6 D (5 boxes)',
  },
  audio_energy: {
    id: 'audio_energy',
    name: 'Audio energy detection',
    detail:
      'getUserMedia + Web Audio AnalyserNode. Detects that the child vocalised at ' +
      'all, which speech recognition cannot report - it returns words or nothing.',
    boxes: 5,
    unlocks: 'Phase 1 B (5 boxes)',
  },
};

export const CAPABILITY_IDS = Object.keys(CAPABILITIES);

/**
 * Validate and normalise a game's capability declaration.
 *
 * @param {Object<string, boolean>} [capabilities]
 * @returns {{ok:boolean, errors:string[], normalised:Object<string,boolean>}}
 *          `normalised` always carries every known capability, defaulting to
 *          false, so callers never have to test for undefined.
 */
export function validateCapabilities(capabilities) {
  const errors = [];
  const normalised = {};

  for (const id of CAPABILITY_IDS) normalised[id] = false;

  if (capabilities === undefined || capabilities === null) {
    return { ok: true, errors, normalised };
  }

  if (typeof capabilities !== 'object' || Array.isArray(capabilities)) {
    return {
      ok: false,
      errors: ['capabilities must be an object of { capabilityId: boolean }'],
      normalised,
    };
  }

  for (const [key, value] of Object.entries(capabilities)) {
    if (!CAPABILITY_IDS.includes(key)) {
      errors.push(
        `Unknown capability "${key}". Valid capabilities: ${CAPABILITY_IDS.join(', ')}. ` +
        'Capabilities exist only for instrumentation-dependent boxes; they cannot be ' +
        'used to exclude observational or directly measurable ones.'
      );
      continue;
    }
    if (typeof value !== 'boolean') {
      errors.push(`Capability "${key}" must be a boolean, got ${typeof value}.`);
      continue;
    }
    normalised[key] = value;
  }

  return { ok: errors.length === 0, errors, normalised };
}

/**
 * The state of a box for a game with these capabilities.
 *
 * @param {object} box a getBoxesForLevel() entry
 * @param {Object<string,boolean>} [capabilities] normalised or raw
 * @returns {'scored'|'observational'|'not_instrumented'}
 */
export function boxState(box, capabilities) {
  if (box.observational) return BOX_STATES.OBSERVATIONAL;
  if (!box.requires) return 'scored';
  // No declaration supplied = the child-global view, where a box is not
  // "inapplicable", it is merely unassessed. Treat it as scoreable.
  if (capabilities === undefined || capabilities === null) return 'scored';
  const { normalised } = validateCapabilities(capabilities);
  return normalised[box.requires] ? 'scored' : BOX_STATES.NOT_INSTRUMENTED;
}

/* =========================================================================
 * THE TEN PHASES
 *
 * Per phase:
 *   names          section 4.1 (clinical, three audiences) + 4.2 (parent)
 *   mlu            section 4.3 MLU Target
 *   operant        section 4.3 Verbal Operant
 *   abaPrinciple   section 4.3 ABA Principle
 *   promptStyle    section 8.2 - style + what the game MUST include
 *   strictness     section 8.3 band id
 *   support        engine ladder indices (see SUPPORT_LADDER note)
 *   gamePromptType the promptType string block-builder.html uses, so a
 *                  block-builder-shaped game maps across with no translation
 *   components     section 6.x - `appliesToLevels` absent means all levels
 *   levels         section 4.3 Level Progression, one entry per row
 * ========================================================================= */

export const PHASES = {
  1: {
    phase: 1,
    name: 'Echoic Foundation',
    parentName: 'First Words',
    abaLabel: 'Echoic',
    slpLabel: 'Vocal Imitation',
    rehabLabel: 'Repetition',
    parentDescription: 'Learning that speaking makes things happen',
    mlu: '1.0',
    operant: 'Echoic',
    abaPrinciple: 'Errorless learning',
    childSays: 'Single words (nouns)',
    goal: 'Learn that voice controls the game',
    promptStyle: {
      style: 'Full prompt + audio',
      mustInclude: 'Pip says "Say [WORD]"',
      detail: '"Say [WORD]" + Pip speaks the word audibly',
    },
    strictness: 'loose',
    support: { baseline: 0, maxSupport: 0, minSupport: 0 },
    gamePromptType: 'full',
    components: [
      { code: 'A', role: 'observational', name: 'Attends to Model',      assesses: 'Child looks/listens when word is spoken', observable: 'Eyes on screen, stops other activity',
        observational: true,
        observationalReason: 'Requires vision. A microphone cannot see whether the child is looking at the screen, and every audio proxy (responded, responded quickly) measures ability rather than attention - so a capable inattentive child would score well and a struggling attentive child badly, inverting what the box is for.' },
      { code: 'B', role: 'attempt', name: 'Attempts Vocalization', assesses: 'Child makes vocal attempt',               observable: 'Any sound in response to prompt',
        requires: 'audio_energy' },
      { code: 'C', role: 'outcome', name: 'Produces Target',       assesses: 'Child produces recognizable word',        observable: 'Word is identifiable by speech recognition' },
    ],
    levels: [
      { level: 1, intent: '1 object (center position)' },
      { level: 2, intent: '1 object (different position)' },
      { level: 3, intent: '2 objects (sequential prompts)' },
      { level: 4, intent: '2 objects (new vocabulary)' },
      { level: 5, intent: '3 objects (more to remember)' },
    ],
  },

  2: {
    phase: 2,
    name: 'Single Words + Fading',
    parentName: 'Growing Independence',
    abaLabel: 'Echoic to Mand',
    slpLabel: 'Single Word Production',
    rehabLabel: 'Naming',
    parentDescription: 'Needing less help to find the right words',
    mlu: '1.0-1.5',
    operant: 'Echoic to Mand',
    abaPrinciple: 'Prompt fading',
    childSays: 'Single words (nouns + verbs)',
    goal: 'Build independence; less help over time',
    promptStyle: {
      style: 'Fading prompts',
      mustInclude: 'Visual to partial to no support',
      detail: 'Fades from partial text to first letter to visual highlight only',
    },
    strictness: 'loose',
    support: { baseline: 1, maxSupport: 0, minSupport: 2 },
    gamePromptType: 'fading',
    components: [
      // All three describe the same event at three points on the fading curve.
      // They separate only if the game escalates support within the trial and
      // records which rung was live when the child spoke.
      { code: 'A', role: 'outcome', name: 'Responds to Prompt Level', assesses: 'Child responds to faded prompt',        observable: 'Appropriate response to partial/visual cue',
        requires: 'prompt_sequence' },
      { code: 'B', role: 'independence', name: 'Retrieves Word',           assesses: 'Child recalls word from memory',        observable: 'Says word without full model',
        requires: 'prompt_sequence' },
      { code: 'C', role: 'independence', name: 'Produces Independently',   assesses: 'Child produces word without support',   observable: 'Clean production, no prompt needed',
        requires: 'prompt_sequence' },
    ],
    levels: [
      { level: 1, intent: 'Partial word shown ("A___")' },
      { level: 2, intent: 'First letter + image highlight' },
      { level: 3, intent: 'Image highlight only' },
      { level: 4, intent: 'Subtle/brief highlight' },
      { level: 5, intent: 'Target pattern only' },
      { level: 6, intent: 'No highlight (full independence)' },
    ],
  },

  3: {
    phase: 3,
    name: 'Two-Word Manding',
    parentName: 'Word Combinations',
    abaLabel: 'Mand',
    slpLabel: 'Two-Word Combinations',
    rehabLabel: 'Phrase Production',
    parentDescription: 'Putting two words together',
    mlu: '1.5-2.0',
    operant: 'Mand',
    abaPrinciple: "Brown's Stage I-II",
    childSays: '"want apple", "put car", "more cookie"',
    goal: 'Combining words to request/command',
    promptStyle: {
      style: 'Visual options',
      mustInclude: 'Word frames, object choices',
      detail: 'Visual word options appear. Child picks and speaks a combination.',
    },
    strictness: 'moderate',
    support: { baseline: 2, maxSupport: 1, minSupport: 3 },
    gamePromptType: 'visual-options',
    components: [
      { code: 'A', role: 'partial', name: 'Carrier Phrase', assesses: 'Uses action word correctly',    observable: '"want", "put", "get", "more"' },
      { code: 'B', role: 'partial', name: 'Object Label',   assesses: 'Uses correct noun',             observable: 'Accurate object identification' },
      { code: 'C', role: 'outcome', name: 'Combination',    assesses: 'Joins words in correct order',  observable: '"want apple" not "apple want"' },
      { code: 'D', role: 'independence', name: 'Spontaneous',    assesses: 'Produces without echoing',      observable: 'Not just repeating what was heard',
        conditional: 'model_opportunity',
        conditionalReason:
          'On a trial where the target was never going to be modelled, "did not echo" is free: '
          + 'there was nothing to echo, so the box scores true and measures nothing. It is assessed '
          + 'only on a trial that both offered a model and produced an utterance.',
        judgementWindow: 'trial',
        note:
          'WINDOW DECISION - the box is judged over THIS TRIAL, not over the session. '
          + 'COMPONENT-JUDGEMENT.md section 5 originally read "no model of this phrase within the '
          + 'last N seconds", which spans trials; ENGINE-BUILD.md section 3.4 required every box on '
          + 'every attempt, which forbids the same section\'s "leave the box unwritten"; and the '
          + 'first game to implement it scored only the current trial, which was neither. Three '
          + 'readings, so one was chosen: the trial. It is what judgePhase2 and judgePhase6D already '
          + 'do, and a box whose window is the trial can be judged from the record that trial '
          + 'produced, while a box whose window is "the last N seconds" needs the PREVIOUS trial\'s '
          + 'record and a clock that outlives it. The cross-trial echo this misses is real and is '
          + 'recorded separately, as trial.priorModelAgoMs - evidence beside the box, never folded '
          + 'into it.' },
    ],
    levels: [
      { level: 1, intent: '2 objects + "want ___" frame' },
      { level: 2, intent: '2 objects + "put ___" frame' },
      { level: 3, intent: '3 objects, verb options shown' },
      { level: 4, intent: '4 objects, similar names (discrimination)' },
      { level: 5, intent: 'Options shown briefly, then fade' },
      { level: 6, intent: 'Gestural only (Pip points)' },
    ],
  },

  4: {
    phase: 4,
    name: 'Early Grammar',
    parentName: 'Grammar Emerges',
    abaLabel: 'Mand + Tact',
    slpLabel: 'Morphology Emergence',
    rehabLabel: 'Grammar Recovery',
    parentDescription: 'Adding "-ing" and small words',
    mlu: '2.0-2.5',
    operant: 'Mand + Tact',
    abaPrinciple: "Brown's Stage II",
    childSays: '"-ing" verbs, prepositions',
    goal: 'Grammar emerges naturally through gameplay',
    promptStyle: {
      style: 'Gestural to Positional',
      mustInclude: 'Grammar hints, position highlights',
      detail: 'Gestural cues, positional hints (grid positions highlighted)',
    },
    strictness: 'moderate',
    support: { baseline: 3, maxSupport: 2, minSupport: 4 },
    gamePromptType: 'grammar',
    components: [
      { code: 'A', role: 'partial', name: 'Verb Morphology', assesses: 'Uses -ing form correctly',    observable: '"putting", "eating", "going"' },
      { code: 'B', role: 'partial', name: 'Prepositions',    assesses: 'Uses in/on/under/next to',    observable: 'Correct spatial language' },
      { code: 'C', role: 'observational', name: 'Articles',        assesses: 'Uses the/a appropriately',    observable: '"the apple", "a car"',
        observational: true,
        observationalReason: 'Speech recognition inserts and deletes "the" and "a" according to its language model, not the acoustics: a child who said "put apple" frequently gets back "put the apple". Scored from recogniser text this box measures the recogniser, not the child. Recovering it needs raw audio and forced alignment, which a browser speech API does not provide.' },
      { code: 'D', role: 'outcome', name: 'Integration',     assesses: 'Combines grammar elements',   observable: 'All pieces together in one utterance' },
    ],
    levels: [
      { level: 1, intent: '"-ing" introduction ("putting apple")' },
      { level: 2, intent: 'Preposition "on" ("put on top")' },
      { level: 3, intent: 'Preposition "in" ("put in middle")' },
      { level: 4, intent: 'Combining elements ("putting apple on top")' },
      { level: 5, intent: 'Articles emerge ("put the car")' },
      { level: 6, intent: 'Positional cues only' },
    ],
  },

  5: {
    phase: 5,
    name: 'Three-Word Phrases',
    parentName: 'Short Phrases',
    abaLabel: 'Mand + Tact',
    slpLabel: 'Phrase Expansion',
    rehabLabel: 'Sentence Building',
    parentDescription: 'Speaking in 3-word phrases',
    mlu: '2.5-3.0',
    operant: 'Mand + Tact',
    abaPrinciple: "Brown's Stage III",
    childSays: '"I want juice", "Pip is eating"',
    goal: 'Speaking in short, complete phrases',
    promptStyle: {
      style: 'Time delay',
      mustInclude: 'Wait before hint appears',
      detail: 'Positional cues fade, time delays introduced (wait before showing hint)',
    },
    strictness: 'standard',
    support: { baseline: 5, maxSupport: 4, minSupport: 5 },
    gamePromptType: 'time-delay',
    components: [
      { code: 'A', role: 'partial', name: 'Subject',            assesses: 'Uses I/you/it appropriately', observable: 'Subject present in phrase' },
      { code: 'B', role: 'partial', name: 'Verb',               assesses: 'Uses action word correctly',  observable: 'Verb present in phrase' },
      { code: 'C', role: 'partial', name: 'Object/Complement',  assesses: 'Completes the phrase',        observable: 'Object or location present' },
      { code: 'D', role: 'outcome', name: 'Word Order',         assesses: 'Correct SVO structure',       observable: 'Subject-Verb-Object order' },
    ],
    levels: [
      { level: 1, intent: '"I want ___" frame shown' },
      { level: 2, intent: '"Put ___ here" frame shown' },
      { level: 3, intent: 'Frame shown after 2-second delay' },
      { level: 4, intent: '4-second delay before hint' },
      { level: 5, intent: 'Hint only on error' },
    ],
  },

  6: {
    phase: 6,
    name: 'Simple Sentences',
    parentName: 'Full Sentences',
    abaLabel: 'Mand + Intraverbal',
    slpLabel: 'Sentence Production',
    rehabLabel: 'Functional Expression',
    parentDescription: 'Complete sentences with confidence',
    mlu: '3.0-3.5',
    operant: 'Mand + Intraverbal',
    abaPrinciple: "Brown's Stage IV",
    childSays: '"Put the apple on the car", "Can I have juice please?"',
    goal: 'Complete, grammatically correct sentences',
    promptStyle: {
      style: 'Natural / situation only',
      mustInclude: 'Pip models once or not at all',
      detail: 'Situation is the only prompt. Pip may model once, then expects sentences.',
    },
    strictness: 'standard',
    support: { baseline: 6, maxSupport: 5, minSupport: 6 },
    gamePromptType: 'natural',
    components: [
      { code: 'A', role: 'outcome', name: 'Complete Structure',    assesses: 'Full S-V-O present',              observable: 'All sentence parts included' },
      { code: 'B', role: 'partial', name: 'Grammatical Accuracy',  assesses: 'Correct morphology and syntax',   observable: 'No grammar errors' },
      { code: 'C', role: 'outcome', name: 'Contextual Fit',        assesses: 'Sentence matches situation',      observable: 'Appropriate to game state' },
      { code: 'D', role: 'independence', name: 'Independence',          assesses: 'Produced without model',          observable: "No imitation of Pip's sentence",
        requires: 'prompt_sequence' },
    ],
    levels: [
      { level: 1, intent: 'Pip models first sentence' },
      { level: 2, intent: 'Pip models, child continues pattern' },
      { level: 3, intent: 'No model, situation only' },
      { level: 4, intent: 'Complex spatial pattern' },
      { level: 5, intent: 'Full independence' },
    ],
  },

  7: {
    phase: 7,
    name: 'Multi-Step Commands',
    parentName: 'Complex Instructions',
    abaLabel: 'Chained Mands',
    slpLabel: 'Complex Directions',
    rehabLabel: 'Sequential Commands',
    parentDescription: 'Multi-step commands',
    mlu: '3.5-4.0',
    operant: 'Chained Mands',
    abaPrinciple: 'Behavioral chaining',
    childSays: '"Put the apple on the car, then put the dog next to the apple"',
    goal: 'Complex, multi-step verbal instructions',
    promptStyle: {
      style: 'None',
      mustInclude: 'Complex patterns, no help',
      detail: 'None. Complex patterns require multi-step planning.',
    },
    strictness: 'flexible',
    support: { baseline: 7, maxSupport: 6, minSupport: 7 },
    gamePromptType: 'chained',
    components: [
      { code: 'A', role: 'partial', name: 'First Command',    assesses: 'Clear, complete first instruction',  observable: 'Understandable first step' },
      { code: 'B', role: 'partial', name: 'Second Command',   assesses: 'Clear, complete second instruction', observable: 'Understandable second step' },
      { code: 'C', role: 'partial', name: 'Connector',        assesses: 'Uses then/and/after',                observable: 'Appropriate linking word' },
      { code: 'D', role: 'outcome', name: 'Logical Sequence', assesses: 'Commands in sensible order',         observable: 'Makes sense spatially/temporally' },
      // Section 6.8 states "(Levels 3-5)" explicitly. 5*4 + 3 = 23 = section 5.2.
      { code: 'E', role: 'partial', name: 'Third Command',    assesses: 'Third instruction',                  observable: 'Clear third step',
        appliesToLevels: [3, 4, 5],
        note: 'Documented in section 6.8 as "(Levels 3-5)". Levels 1-2 are two-step only, so there is no third command to assess.' },
    ],
    levels: [
      { level: 1, intent: '2 steps, pause allowed' },
      { level: 2, intent: '2 steps, one utterance' },
      { level: 3, intent: '3 steps with pause' },
      { level: 4, intent: '3 steps, one utterance' },
      { level: 5, intent: 'Complex sequence (logical ordering required)' },
    ],
  },

  8: {
    phase: 8,
    name: 'Conversational',
    parentName: 'Conversation Skills',
    abaLabel: 'Intraverbal',
    slpLabel: 'Pragmatic Response',
    rehabLabel: 'Conversational Response',
    parentDescription: 'Answering questions naturally',
    mlu: '4.0+',
    operant: 'Intraverbal',
    abaPrinciple: 'Social reciprocity',
    childSays: "Responses to Pip's questions",
    goal: 'Responding appropriately to conversational prompts',
    promptStyle: {
      style: 'Pip initiates',
      mustInclude: 'Questions for child to answer',
      detail: 'Pip asks questions. Child responds naturally.',
    },
    strictness: 'flexible',
    support: { baseline: 7, maxSupport: 6, minSupport: 7 },
    gamePromptType: 'conversational',
    components: [
      { code: 'A', role: 'observational', name: 'Attends',         assesses: 'Listens to/processes question', observable: 'Pauses, engages with question',
        observational: true,
        observationalReason: 'Same problem as Phase 1 A. Response latency is not attention - a child can answer instantly without having listened, or attend closely and answer slowly.' },
      { code: 'B', role: 'outcome', name: 'Comprehends',     assesses: 'Understands question type',     observable: 'Response matches question type' },
      { code: 'C', role: 'partial', name: 'Formulates',      assesses: 'Creates appropriate response',  observable: 'Constructs answer' },
      { code: 'D', role: 'partial', name: 'Delivers',        assesses: 'Produces clear response',       observable: 'Understandable speech' },
      // ENGINE DECISION - see the header note. Not stated in the spec.
      { code: 'E', role: 'partial', name: 'Maintains Topic', assesses: 'Stays relevant to exchange',    observable: "Doesn't go off-topic",
        appliesToLevels: [3, 4, 5, 6],
        note: 'ENGINE DECISION, not spec text. Section 5.2 requires 28 boxes over 6 levels; section 6.9 does not say which levels carry E. Levels 3-6 mirrors Phase 7\'s documented pattern and is the only allocation that is both clinically coherent and arithmetically exact. Levels 1-2 (yes/no and single "what" answers) are too short an exchange for topic maintenance to be observable.' },
    ],
    levels: [
      { level: 1, intent: 'Yes/No questions' },
      { level: 2, intent: '"What" questions' },
      { level: 3, intent: '"Where" questions' },
      { level: 4, intent: 'Planning questions ("What should we do first?")' },
      { level: 5, intent: '"Why" questions (reasoning)' },
      { level: 6, intent: 'Multi-turn conversation' },
    ],
  },

  9: {
    phase: 9,
    name: 'Child-Initiated',
    parentName: 'Starting Conversations',
    abaLabel: 'Full Intraverbal',
    slpLabel: 'Spontaneous Initiation',
    rehabLabel: 'Self-Initiated Speech',
    parentDescription: 'Initiating speech independently',
    mlu: '4.0+',
    operant: 'Full Intraverbal',
    abaPrinciple: 'Spontaneous initiation',
    childSays: 'Child asks Pip questions, directs without prompting',
    goal: 'Spontaneous speech initiation',
    promptStyle: {
      style: 'Pip waits',
      mustInclude: 'Silence is the prompt',
      detail: 'Pip waits. Silence is the prompt.',
    },
    strictness: 'flexible',
    support: { baseline: 7, maxSupport: 6, minSupport: 7 },
    gamePromptType: 'child-initiated',
    components: [
      { code: 'A', role: 'outcome', name: 'Initiates',                assesses: 'Begins without prompt',   observable: 'Speaks first without waiting' },
      { code: 'B', role: 'outcome', name: 'Contextually Appropriate', assesses: 'Fits the situation',      observable: 'Makes sense in game context' },
      { code: 'C', role: 'partial', name: 'Clear Communication',      assesses: 'Understandable message',  observable: 'Can be understood' },
      { code: 'D', role: 'partial', name: 'Sustains Exchange',        assesses: 'Continues interaction',   observable: 'Multiple turns if appropriate' },
    ],
    levels: [
      { level: 1, intent: 'Pip says "I\'m ready!" then waits' },
      { level: 2, intent: 'Pip waits silently' },
      { level: 3, intent: 'Open pattern (child asks OR directs)' },
      { level: 4, intent: 'Pip makes "mistake" (child corrects)' },
      { level: 5, intent: 'Full direction (child runs entire interaction)' },
    ],
  },

  10: {
    phase: 10,
    name: 'Free Play',
    parentName: 'Creative Communication',
    abaLabel: 'Generalization',
    slpLabel: 'Functional Communication',
    rehabLabel: 'Generalized Communication',
    parentDescription: 'Free expression and generalization',
    mlu: 'Variable',
    operant: 'All operants',
    abaPrinciple: 'Generalization',
    childSays: 'Any valid command, creative expression',
    goal: 'Transfer skills to novel situations; creativity',
    promptStyle: {
      style: 'Open sandbox',
      mustInclude: 'Child creates own goals',
      detail: 'Open sandbox. Child creates their own goals.',
    },
    strictness: 'flexible',
    support: { baseline: 7, maxSupport: 7, minSupport: 7 },
    gamePromptType: 'free',
    components: [
      { code: 'A', role: 'outcome', name: 'Creative Expression',  assesses: 'Novel/original communication',    observable: 'Not just repeating learned phrases' },
      { code: 'B', role: 'partial', name: 'Varied Vocabulary',    assesses: 'Uses diverse words',              observable: 'Range of nouns, verbs, etc.' },
      { code: 'C', role: 'partial', name: 'Appropriate Structure', assesses: 'Grammar fits context',           observable: 'Correct for complexity level' },
      { code: 'D', role: 'observational', name: 'Generalized',    assesses: 'Transfers skills to new situations', observable: 'Uses skills flexibly',
        observational: true,
        observationalReason: 'Not a property of an utterance at all. "Transfers skills to new situations" is section 9.3\'s cross-game verification - the same level Green in two or more different games - which is a fact about the CHART and is already computed there by checkGeneralization(). No single play can evidence it, so any per-play verdict is a proxy a game invented, and four boxes would read red for ever in every game that reached Phase 10. It is reported where it is real, beside the chart, never scored from a play. See docs/audit/COMPONENT-JUDGEMENT.md section 5 and docs/audit/FRAMEWORK-GAPS.md section 1.' },
    ],
    levels: [
      { level: 1, intent: 'Empty grid, all objects ("Build whatever you want!")' },
      { level: 2, intent: 'Themed objects (creative building with category)' },
      { level: 3, intent: 'Build then describe ("Tell me about what you made!")' },
      { level: 4, intent: 'Unlimited sandbox (pure creative play)' },
    ],
  },
};

export const PHASE_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
export const FIRST_PHASE = 1;
export const LAST_PHASE = 10;

/* =========================================================================
 * BOX ID
 * ========================================================================= */

const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Build the permanent id for one box.
 * @param {number} phase 1-10
 * @param {number} level 1-based row within the phase
 * @param {string} code  single uppercase component letter
 * @returns {string} e.g. "P08L06E"
 */
export function makeBoxId(phase, level, code) {
  return `P${pad2(phase)}L${pad2(level)}${code}`;
}

/**
 * Parse a box id back into its parts. Returns null if malformed.
 * @param {string} boxId
 * @returns {{phase:number, level:number, code:string}|null}
 */
export function parseBoxId(boxId) {
  const m = /^P(\d{2})L(\d{2})([A-Z])$/.exec(String(boxId || ''));
  if (!m) return null;
  return { phase: Number(m[1]), level: Number(m[2]), code: m[3] };
}

/* =========================================================================
 * ACCESSORS
 * ========================================================================= */

/**
 * @param {number} phase 1-10
 * @returns {object|null} the phase record, or null if out of range
 */
export function getPhase(phase) {
  return PHASES[phase] || null;
}

/**
 * @param {number} phase 1-10
 * @param {number} level 1-based
 * @returns {object|null} { phase, level, intent, phaseName, parentName, boxCount, ... }
 */
export function getLevel(phase, level) {
  const p = getPhase(phase);
  if (!p) return null;
  const row = p.levels.find((l) => l.level === level);
  if (!row) return null;
  return {
    phase,
    level,
    intent: row.intent,
    phaseName: p.name,
    parentName: p.parentName,
    mlu: p.mlu,
    operant: p.operant,
    strictness: p.strictness,
    promptStyle: p.promptStyle,
    gamePromptType: p.gamePromptType,
    support: p.support,
    boxCount: getBoxesForLevel(phase, level).length,
  };
}

/**
 * The boxes a game must measure for one level.
 *
 * A game stage MUST record a result for every box this returns
 * (section 8.1, rule 1). Returning 5 for Phase 7 Level 4 and 4 for Phase 7
 * Level 1 is correct, not a bug - see the box arithmetic note in the header.
 *
 * @param {number} phase 1-10
 * @param {number} level 1-based
 * @returns {Array<{boxId:string, phase:number, level:number, code:string,
 *                  name:string, assesses:string, observable:string,
 *                  position:number, note?:string}>}
 *          Empty array if the phase/level does not exist.
 */
export function getBoxesForLevel(phase, level) {
  const p = getPhase(phase);
  if (!p) return [];
  if (!p.levels.some((l) => l.level === level)) return [];

  return p.components
    .filter((c) => !c.appliesToLevels || c.appliesToLevels.includes(level))
    .map((c, i) => ({
      boxId: makeBoxId(phase, level, c.code),
      phase,
      level,
      code: c.code,
      name: c.name,
      assesses: c.assesses,
      observable: c.observable,
      // What this box is FOR - see COMPONENT_ROLES. Pacing reads it; the chart
      // does not.
      role: c.role,
      position: i,
      // Permanent: no browser input can produce this judgement. Never scored,
      // never counted toward a level colour, never a failure.
      observational: Boolean(c.observational),
      ...(c.observationalReason ? { observationalReason: c.observationalReason } : {}),
      // Per-game: the capability a game must declare to score this box.
      ...(c.requires ? { requires: c.requires } : {}),
      // Per-TRIAL: assessable only on trials that can answer the question.
      // The engine decides; see CONDITIONALS.
      ...(c.conditional ? { conditional: c.conditional } : {}),
      ...(c.conditionalReason ? { conditionalReason: c.conditionalReason } : {}),
      ...(c.judgementWindow ? { judgementWindow: c.judgementWindow } : {}),
      ...(c.note ? { note: c.note } : {}),
    }));
}

/**
 * The boxes a game with these capabilities must actually measure.
 *
 * This is the set `scoring.scoreAttempt()` validates against - NOT
 * getBoxesForLevel(), which returns the full chart row including boxes no
 * game can score. Observational boxes are always excluded; instrumentation
 * boxes are excluded when the capability is not declared.
 *
 * CAPABILITIES ARGUMENT - the two absent cases differ, deliberately:
 *   `{}` or `{prompt_sequence:false}` - an explicit declaration by a game that
 *        it does not instrument this. The box is excluded.
 *   `undefined` / `null` - no game context at all (the child-global chart).
 *        Instrumentation boxes are INCLUDED, because globally such a box is
 *        unassessed rather than inapplicable.
 * A caller that forgets to pass a game's declaration therefore over-includes
 * and fails loudly with a coverage error, rather than silently under-measuring.
 *
 * @param {number} phase
 * @param {number} level
 * @param {Object<string,boolean>} [capabilities]
 * @returns {Array<object>} subset of getBoxesForLevel()
 */
export function getAssessableBoxes(phase, level, capabilities) {
  return getBoxesForLevel(phase, level)
    .filter((box) => boxState(box, capabilities) === 'scored');
}

/**
 * Every box in the framework, in chart order.
 * @returns {Array<object>} 208 entries
 */
export function getAllBoxes() {
  const out = [];
  for (const phase of PHASE_NUMBERS) {
    for (const row of PHASES[phase].levels) {
      out.push(...getBoxesForLevel(phase, row.level));
    }
  }
  return out;
}

/**
 * Every level in the framework, in chart order.
 * @returns {Array<{phase:number, level:number}>} 53 entries
 */
export function getAllLevels() {
  const out = [];
  for (const phase of PHASE_NUMBERS) {
    for (const row of PHASES[phase].levels) out.push({ phase, level: row.level });
  }
  return out;
}

/* =========================================================================
 * GAME COVERAGE (section 8.7)
 *
 * "Not every game needs all 10 phases." Coverage has TWO dimensions and the
 * spec only names one of them:
 *
 *   phasesAvailable   which phases the game implements  - section 8.7
 *   levelsAvailable   which LEVELS within them          - not in the spec
 *
 * The second is not an edge case. A game built to be played with a child in
 * days implements a slice of a phase, not all six levels of it, and without a
 * declaration the engine places children on levels that do not exist and
 * recommends advancing to them. Every game then re-clamps the answer at every
 * call site, differently. So coverage is modelled here, once.
 *
 * Both are optional and either may be given alone:
 *   - `levelsAvailable` alone implies its phases.
 *   - `phasesAvailable` alone means every level of those phases.
 *   - Both, disagreeing, is an error rather than a guess.
 * ========================================================================= */

/** The 53 levels in chart order. Cached: navigation walks it constantly. */
const LEVEL_SEQUENCE = Object.freeze(
  PHASE_NUMBERS.flatMap((phase) =>
    PHASES[phase].levels.map((row) => Object.freeze({ phase, level: row.level }))
  )
);

/**
 * Validate and normalise a game's coverage declaration.
 *
 * @param {{phasesAvailable?:number[], levelsAvailable?:Array<{phase:number, level:number}>}} [coverage]
 * @returns {{ok:boolean, errors:string[], availablePhases:number[]|null,
 *            availableLevels:Array<{phase:number, level:number}>|null,
 *            levelCount:number}}
 *          Nulls mean "no restriction", which is what the whole framework is.
 */
export function validateCoverage(coverage) {
  const errors = [];
  if (!coverage) {
    return { ok: true, errors, availablePhases: null, availableLevels: null, levelCount: LEVEL_SEQUENCE.length };
  }

  const { phasesAvailable, levelsAvailable } = coverage;
  let phases = null;
  let levels = null;

  if (phasesAvailable !== undefined && phasesAvailable !== null) {
    if (!Array.isArray(phasesAvailable) || !phasesAvailable.length) {
      errors.push('phasesAvailable must be a non-empty array of phase numbers.');
    } else {
      for (const phase of phasesAvailable) {
        if (!getPhase(phase)) errors.push(`phasesAvailable contains phase ${phase}, which does not exist.`);
      }
      phases = [...new Set(phasesAvailable)].sort((a, b) => a - b);
    }
  }

  if (levelsAvailable !== undefined && levelsAvailable !== null) {
    if (!Array.isArray(levelsAvailable) || !levelsAvailable.length) {
      errors.push('levelsAvailable must be a non-empty array of { phase, level }.');
    } else {
      const seen = new Set();
      levels = [];
      for (const entry of levelsAvailable) {
        if (!entry || !Number.isInteger(entry.phase) || !Number.isInteger(entry.level)) {
          errors.push('Every levelsAvailable entry must be { phase:int, level:int }.');
          continue;
        }
        if (!getLevel(entry.phase, entry.level)) {
          errors.push(`levelsAvailable contains phase ${entry.phase} level ${entry.level}, which does not exist.`);
          continue;
        }
        const key = `${entry.phase}.${entry.level}`;
        if (seen.has(key)) continue;
        seen.add(key);
        levels.push({ phase: entry.phase, level: entry.level });
      }
      levels.sort((a, b) => (a.phase - b.phase) || (a.level - b.level));

      if (phases) {
        // Disagreement is a bug in the declaration, not something to reconcile.
        for (const entry of levels) {
          if (!phases.includes(entry.phase)) {
            errors.push(
              `levelsAvailable includes phase ${entry.phase} level ${entry.level}, but phase `
              + `${entry.phase} is not in phasesAvailable. The two declarations must agree.`
            );
          }
        }
        for (const phase of phases) {
          if (!levels.some((l) => l.phase === phase)) {
            errors.push(
              `phasesAvailable includes phase ${phase}, but levelsAvailable has no level in it. `
              + 'Declare at least one level per phase, or drop the phase.'
            );
          }
        }
      } else {
        // levelsAvailable alone implies its phases.
        phases = [...new Set(levels.map((l) => l.phase))].sort((a, b) => a - b);
      }
    }
  }

  return {
    ok: errors.length === 0,
    errors,
    availablePhases: errors.length ? null : phases,
    availableLevels: errors.length ? null : levels,
    levelCount: levels ? levels.length
      : (phases ? LEVEL_SEQUENCE.filter((l) => phases.includes(l.phase)).length : LEVEL_SEQUENCE.length),
  };
}

/**
 * Where a level sits in the 53-level chart order.
 *
 * The framework is a SEQUENCE, and "further on" is a question that comes up
 * wherever two positions have to be compared - which of two levels is ahead,
 * whether a child has moved forward or back, what their furthest point is. Doing
 * that arithmetic on (phase, level) pairs by hand gets it wrong at every phase
 * boundary, because the phases are different lengths.
 *
 * @param {number} phase
 * @param {number} level
 * @returns {number} 0..52, or -1 if there is no such level
 */
export function levelIndex(phase, level) {
  return LEVEL_SEQUENCE.findIndex((l) => l.phase === phase && l.level === level);
}

/**
 * Compare two positions in chart order.
 * @returns {number|null} negative if a is earlier, 0 if equal, positive if a is
 *          later; null if either position does not exist.
 */
export function compareLevels(a, b) {
  const ai = a ? levelIndex(a.phase, a.level) : -1;
  const bi = b ? levelIndex(b.phase, b.level) : -1;
  if (ai === -1 || bi === -1) return null;
  return ai - bi;
}

/**
 * Is this level one the caller's coverage includes?
 *
 * @param {number} phase
 * @param {number} level
 * @param {{availablePhases?:number[]|null, availableLevels?:Array|null}} [opts]
 * @returns {boolean}
 */
export function isLevelAvailable(phase, level, opts = {}) {
  if (!getLevel(phase, level)) return false;
  const { availablePhases, availableLevels } = opts;
  if (availablePhases && !availablePhases.includes(phase)) return false;
  if (availableLevels && !availableLevels.some((l) => l.phase === phase && l.level === level)) return false;
  return true;
}

/**
 * The next level in the global progression, crossing the phase boundary.
 *
 * @param {number} phase
 * @param {number} level
 * @param {{availablePhases?: number[], availableLevels?: Array<{phase:number, level:number}>}} [opts]
 *        restrict to a game's coverage (section 8.7). Skips forward past
 *        anything the game does not implement - a phase OR a single level.
 * @returns {{phase:number, level:number}|null} null at the end of the
 *          framework (Phase 10 Level 4) or when nothing further is available.
 */
export function getNextLevel(phase, level, opts = {}) {
  const from = LEVEL_SEQUENCE.findIndex((l) => l.phase === phase && l.level === level);
  if (from === -1) return null;
  for (let i = from + 1; i < LEVEL_SEQUENCE.length; i += 1) {
    const candidate = LEVEL_SEQUENCE[i];
    if (isLevelAvailable(candidate.phase, candidate.level, opts)) {
      return { phase: candidate.phase, level: candidate.level };
    }
  }
  return null;
}

/**
 * The previous level in the global progression, crossing the phase boundary.
 * Used by progression.js when suggesting a step back (section 8.4).
 *
 * @param {number} phase
 * @param {number} level
 * @param {{availablePhases?: number[], availableLevels?: Array<{phase:number, level:number}>}} [opts]
 * @returns {{phase:number, level:number}|null} null at Phase 1 Level 1.
 */
export function getPreviousLevel(phase, level, opts = {}) {
  const from = LEVEL_SEQUENCE.findIndex((l) => l.phase === phase && l.level === level);
  if (from === -1) return null;
  for (let i = from - 1; i >= 0; i -= 1) {
    const candidate = LEVEL_SEQUENCE[i];
    if (isLevelAvailable(candidate.phase, candidate.level, opts)) {
      return { phase: candidate.phase, level: candidate.level };
    }
  }
  return null;
}

/**
 * The nearest level a game implements, at or below (phase, level).
 *
 * Downward, never upward, and this is not a preference: section 2.4 is
 * unambiguous that a child placed above their level fails repeatedly, and
 * failure builds avoidance. Being placed slightly low costs a few easy
 * successes, which is the cheaper mistake. Only when there is nothing at all
 * below does it look upward, because the alternative is no content.
 *
 * @returns {{phase:number, level:number, clamped:boolean, direction:'none'|'down'|'up'}|null}
 */
export function nearestAvailableLevel(phase, level, opts = {}) {
  if (isLevelAvailable(phase, level, opts)) {
    return { phase, level, clamped: false, direction: 'none' };
  }
  const below = getPreviousLevel(phase, level, opts);
  if (below) return { ...below, clamped: true, direction: 'down' };
  const above = getNextLevel(phase, level, opts);
  if (above) return { ...above, clamped: true, direction: 'up' };
  return null;
}

/**
 * The prompt style a game must implement for a phase (section 8.2).
 *
 * @param {number} phase 1-10
 * @returns {{phase:number, style:string, mustInclude:string, detail:string,
 *            gamePromptType:string, support:object, ladder:object}|null}
 */
export function getPromptStyle(phase) {
  const p = getPhase(phase);
  if (!p) return null;
  return {
    phase,
    style: p.promptStyle.style,
    mustInclude: p.promptStyle.mustInclude,
    detail: p.promptStyle.detail,
    gamePromptType: p.gamePromptType,
    support: p.support,
    ladder: SUPPORT_LADDER[p.support.baseline],
  };
}

/**
 * The recognition strictness band for a phase (section 8.3).
 *
 * @param {number} phase 1-10
 * @returns {{phase:number, id:string, phases:number[], accepts:string,
 *            rules:object}|null}
 */
export function getStrictness(phase) {
  const p = getPhase(phase);
  if (!p) return null;
  const band = STRICTNESS_BANDS[p.strictness];
  return { phase, ...band };
}

/**
 * Look up one support rung by ladder index, clamped into range.
 * @param {number} index
 * @returns {object} a SUPPORT_LADDER entry
 */
export function getSupportLevel(index) {
  const i = Math.max(0, Math.min(SUPPORT_LADDER.length - 1, Math.round(index)));
  return SUPPORT_LADDER[i];
}

/* =========================================================================
 * SHAPE - the counts, computed rather than asserted by hand
 * ========================================================================= */

export const FRAMEWORK_SHAPE = (() => {
  const perPhase = PHASE_NUMBERS.map((phase) => {
    const p = PHASES[phase];
    const rows = p.levels.flatMap((row) => getBoxesForLevel(phase, row.level));
    return {
      phase,
      levels: p.levels.length,
      boxes: rows.length,
      observational: rows.filter((b) => b.observational).length,
      instrumentation: rows.filter((b) => b.requires).length,
      conditional: rows.filter((b) => b.conditional).length,
    };
  });
  const total = perPhase.reduce((n, x) => n + x.boxes, 0);
  const observational = perPhase.reduce((n, x) => n + x.observational, 0);
  const instrumentation = perPhase.reduce((n, x) => n + x.instrumentation, 0);
  const conditional = perPhase.reduce((n, x) => n + x.conditional, 0);
  return {
    phases: PHASE_NUMBERS.length,
    levels: perPhase.reduce((n, x) => n + x.levels, 0),
    boxes: total,
    // Never scoreable by any game, in any configuration.
    observational,
    // Scoreable only by a game declaring the matching capability.
    instrumentation,
    // Scoreable, but only on trials that can answer the question they ask.
    conditional,
    // Scoreable by any game, with no declaration needed.
    alwaysScoreable: total - observational - instrumentation,
    perPhase,
  };
})();

/* Fail loudly at import time rather than shipping a wrong chart. */
(() => {
  const { phases, levels, boxes } = FRAMEWORK_SHAPE;
  const errs = [];
  if (phases !== 10) errs.push(`phases: expected 10, got ${phases}`);
  if (levels !== 53) errs.push(`levels: expected 53, got ${levels}`);
  if (boxes !== 208) errs.push(`boxes: expected 208, got ${boxes}`);

  // Per-phase totals from section 5.2.
  const expected = { 1: 15, 2: 18, 3: 24, 4: 24, 5: 20, 6: 20, 7: 23, 8: 28, 9: 20, 10: 16 };
  for (const row of FRAMEWORK_SHAPE.perPhase) {
    if (row.boxes !== expected[row.phase]) {
      errs.push(`phase ${row.phase}: expected ${expected[row.phase]} boxes, got ${row.boxes}`);
    }
  }

  // Ids must be unique - a collision would silently merge two skills in the DB.
  const ids = getAllBoxes().map((b) => b.boxId);
  if (new Set(ids).size !== ids.length) errs.push('duplicate box ids');

  // The observational and instrumentation sets are load-bearing: they decide
  // which boxes can ever go Green. Lock their sizes so a stray edit is caught
  // at import, the same way the 10/53/208 counts are.
  // 21, not 17: Phase 10 D (Generalized) joined the set - see the header note.
  if (FRAMEWORK_SHAPE.observational !== 21) {
    errs.push(`observational boxes: expected 21, got ${FRAMEWORK_SHAPE.observational}`);
  }
  if (FRAMEWORK_SHAPE.instrumentation !== 28) {
    errs.push(`instrumentation boxes: expected 28, got ${FRAMEWORK_SHAPE.instrumentation}`);
  }
  // Phase 3 D at all six levels. Locked like the other two sets: which boxes
  // can be excluded from a window, and on what grounds, decides what a colour
  // means, so a stray edit must fail at import rather than quietly change it.
  if (FRAMEWORK_SHAPE.conditional !== 6) {
    errs.push(`conditional boxes: expected 6, got ${FRAMEWORK_SHAPE.conditional}`);
  }
  const unknownCond = getAllBoxes().filter((b) => b.conditional && !CONDITIONAL_IDS.includes(b.conditional));
  if (unknownCond.length) {
    errs.push(`boxes with an unknown condition: ${unknownCond.map((b) => `${b.boxId}:${b.conditional}`).join(', ')}`);
  }
  // A conditional box must still be scoreable in principle - marking an
  // observational box conditional would be a contradiction, not a refinement.
  const condObs = getAllBoxes().filter((b) => b.conditional && b.observational);
  if (condObs.length) {
    errs.push(`observational boxes cannot also be conditional: ${condObs.map((b) => b.boxId).join(', ')}`);
  }
  // A box cannot be both: observational is permanent, instrumentation is a
  // game's choice, and a box that is both would be unreachable but look
  // reachable to a game that declared the capability.
  const both = getAllBoxes().filter((b) => b.observational && b.requires);
  if (both.length) {
    errs.push(`boxes marked both observational and instrumentation-dependent: ${both.map((b) => b.boxId).join(', ')}`);
  }
  const roleless = getAllBoxes().filter((b) => !ROLE_IDS.includes(b.role));
  if (roleless.length) {
    errs.push(`boxes with no declared role: ${roleless.map((b) => b.boxId).join(', ')}`);
  }
  // A level with no outcome box has nothing for pacing to read, so section 8.4
  // would have no definition of success at it.
  for (const { phase, level } of getAllLevels()) {
    const row = getBoxesForLevel(phase, level);
    if (!row.some((b) => b.role === COMPONENT_ROLES.OUTCOME)) {
      errs.push(`phase ${phase} level ${level} has no outcome box`);
    }
  }
  const unknownReq = getAllBoxes().filter((b) => b.requires && !CAPABILITY_IDS.includes(b.requires));
  if (unknownReq.length) {
    errs.push(`boxes requiring an unknown capability: ${unknownReq.map((b) => `${b.boxId}:${b.requires}`).join(', ')}`);
  }

  if (errs.length) {
    throw new Error(
      `framework.js violates ${SPEC_DOCUMENT} section 5.1/5.2:\n  - ${errs.join('\n  - ')}`
    );
  }
})();

export default {
  FRAMEWORK_VERSION,
  SPEC_DOCUMENT,
  COLORS,
  COLOR_ORDER,
  SCORING_WINDOW,
  SUPPORT_LADDER,
  SUPPORT_BY_ID,
  STRICTNESS_BANDS,
  PHASES,
  PHASE_NUMBERS,
  FIRST_PHASE,
  LAST_PHASE,
  FRAMEWORK_SHAPE,
  makeBoxId,
  parseBoxId,
  getPhase,
  getLevel,
  getBoxesForLevel,
  getAssessableBoxes,
  boxState,
  validateCapabilities,
  BOX_STATES,
  CAPABILITIES,
  CAPABILITY_IDS,
  CONDITIONALS,
  CONDITIONAL_IDS,
  COMPONENT_ROLES,
  ROLE_IDS,
  getAllBoxes,
  getAllLevels,
  validateCoverage,
  isLevelAvailable,
  nearestAvailableLevel,
  levelIndex,
  compareLevels,
  getNextLevel,
  getPreviousLevel,
  getPromptStyle,
  getStrictness,
  getSupportLevel,
};
